<template>
  <b-overlay :show="checkingExisting" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>
    <transition name="bounce">
      <b-container class="py-5 custom-container container">
        <h2 class="pl-0 pt-5 mt-5 font-weight-bold">File Upload</h2>
        <h3 class="pl-0 mt-5 font-weight-bold">Template Section</h3>
        <p class="pl-0 text-secondary">
          Select from the types of file you want to upload below or upload a new
          document
        </p>
        <b-row>
          <b-col :lg="3" :md="4" :sm="6" :cols="12">
            <b-card no-body class="template-card first" @click="newFilePage">
              <div class="template-card__inner">
                <b-img-lazy
                  center
                  :src="require('@/assets/icons/add.png')"
                  class="template-card__icon"
                ></b-img-lazy>
                <div class="template-card__text">
                  <div class="template-card__title">New Upload</div>
                  <div class="template-card__desc">
                    Create without a template
                  </div>
                </div>
              </div>
            </b-card>
          </b-col>
          <b-col
            :lg="3"
            :md="4"
            :sm="6"
            :cols="12"
            class="mb-4"
            v-for="template in templates"
            :key="template.id"
          >
            <template-item
              :template="template"
              @click="templateForm(template)"
            />
          </b-col>
        </b-row>

        <existing-file-modal
          :show-modal="showExistingFileModal"
          :files="existingFiles"
          :subject="existingFilesSubject"
          :show-company-approval="isBusinessAdminUpload"
          :template-label="pendingTemplate ? pendingTemplate.label : 'file'"
          @close="closeExistingFileModal"
          @createNew="onCreateNewFile"
          @edit="onEditExistingFile"
          @view="onViewExistingFile"
        />
      </b-container>
    </transition>
  </b-overlay>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import store from '@/store'
import fileDuplicateMatch from '@/mixins/fileDuplicateMatch'
import {
  tradieUploadContextQuery,
  clearTradieUploadContextUnlessInFlow,
} from '@/utils/tradieUploadContext'
import TemplateItem from './TemplateItem'
import ExistingFileModal from '@/components/modals/ExistingFileModal'

function clearTradieContextIfNeeded(from) {
  clearTradieUploadContextUnlessInFlow(from)
}

export default {
  mixins: [fileDuplicateMatch],
  components: {
    TemplateItem,
    ExistingFileModal,
  },
  data() {
    return {
      loading: false,
      checkingExisting: false,
      showExistingFileModal: false,
      existingFiles: [],
      pendingTemplate: null,
    }
  },
  computed: {
    ...mapGetters('template', ['templates']),
    ...mapGetters('requests', ['request']),

    isRequestFileCreation() {
      return this.$route.name === 'request.request-file-templates-page'
    },
  },

  beforeRouteEnter(to, from, next) {
    clearTradieContextIfNeeded(from)

    if (to.name !== 'request.request-file-templates-page') {
      next()
      return
    }

    store
      .dispatch('requests/getRequestByUser', to.params.fileRequestUserId)
      .then((request) => {
        if (request.file_request && request.file_request.template) {
          next({
            name: 'request.request-file-template-form-page',
            params: {
              fileRequestUserId: to.params.fileRequestUserId,
              id: String(request.file_request.template.id),
            },
            replace: true,
          })
        } else {
          next()
        }
      })
      .catch((err) => {
        if (err && err.data && err.data.status === 404) {
          next({ name: 'page-not-found', replace: true })
        } else {
          next()
        }
      })
  },

  beforeRouteUpdate(to, from, next) {
    clearTradieContextIfNeeded(from)

    if (to.name !== 'request.request-file-templates-page') {
      next()
      return
    }

    store
      .dispatch('requests/getRequestByUser', to.params.fileRequestUserId)
      .then((request) => {
        if (request.file_request && request.file_request.template) {
          next({
            name: 'request.request-file-template-form-page',
            params: {
              fileRequestUserId: to.params.fileRequestUserId,
              id: String(request.file_request.template.id),
            },
            replace: true,
          })
        } else {
          next((vm) => vm.loadTemplatesForCurrentRoute())
        }
      })
      .catch((err) => {
        if (err && err.data && err.data.status === 404) {
          next({ name: 'page-not-found', replace: true })
        } else {
          next((vm) => vm.loadTemplatesForCurrentRoute())
        }
      })
  },

  methods: {
    ...mapActions('template', ['getTemplates']),
    ...mapActions('requests', ['getRequestByUser']),

    async fetchTemplates() {
      this.loading = true
      try {
        await this.getTemplates()
      } catch (e) {
        this.$bvToast.toast('Could not load templates. Please try again.', {
          variant: 'danger',
          title: 'Error',
        })
      } finally {
        this.loading = false
      }
    },
    newFilePage() {
      if (this.isRequestFileCreation) {
        this.$router.replace({
          name: 'request.request-file-new-page',
          params: {
            fileRequestUserId: this.$route.params.fileRequestUserId,
          },
        })
      } else {
        this.$router.replace({
          name: 'file.new-page',
          query: tradieUploadContextQuery(),
        })
      }
    },
    async templateForm(template) {
      if (this.checkingExisting) return

      if (this.isRequestFileCreation) {
        this.$router.replace({
          name: 'request.request-file-template-form-page',
          params: {
            fileRequestUserId: this.$route.params.fileRequestUserId,
            id: template.id,
          },
        })
        return
      }

      // A tradie creating a file for themselves, a business admin uploading for
      // a tradie, or a business admin adding a company file: warn if a file of
      // this type already exists before letting them create another one. A
      // page-level loader runs while we gather the files; the modal only opens
      // when there's actually a match.
      if (
        this.isTradie ||
        this.isBusinessAdminUploadingForTradie ||
        this.isBusinessAdminCompanyUpload
      ) {
        this.checkingExisting = true
        let matches = []
        try {
          matches = await this.findExistingFiles(template)
        } catch (err) {
          matches = []
        } finally {
          this.checkingExisting = false
        }

        if (matches.length) {
          this.existingFiles = matches
          this.pendingTemplate = template
          this.showExistingFileModal = true
          return
        }
      }

      this.goToTemplateForm(template)
    },

    goToTemplateForm(template) {
      this.$router.replace({
        name: 'template.form-page',
        params: {
          id: template.id,
        },
        query: tradieUploadContextQuery(),
      })
    },

    closeExistingFileModal() {
      this.showExistingFileModal = false
      this.existingFiles = []
      this.pendingTemplate = null
    },

    onCreateNewFile() {
      const template = this.pendingTemplate
      this.closeExistingFileModal()
      if (template) this.goToTemplateForm(template)
    },

    onEditExistingFile(file) {
      const target = file || this.existingFiles[0]
      this.closeExistingFileModal()
      if (!target) return
      const id = target.file_id ? target.file_id : target.id
      this.$router.replace({
        name: 'file.edit-page',
        params: { id },
      })
    },

    onViewExistingFile(file) {
      const target = file || this.existingFiles[0]
      this.closeExistingFileModal()
      if (!target) return
      const id = target.file_id ? target.file_id : target.id
      this.$router.replace({
        name: 'file.view-page',
        params: { id },
      })
    },

    async loadTemplatesForCurrentRoute() {
      if (this.isRequestFileCreation) {
        const fruid = this.$route.params.fileRequestUserId
        const hasRequest =
          this.request && String(this.request.id) === String(fruid)

        if (!hasRequest) {
          try {
            await this.getRequestByUser(fruid)
          } catch (err) {
            if (err.data && err.data.status === 404) {
              this.$router.replace({ name: 'page-not-found' })
            }
            return
          }
          if (this.request.file_request && this.request.file_request.template) {
            this.templateForm(this.request.file_request.template)
            return
          }
        }

        await this.fetchTemplates()
      } else {
        await this.fetchTemplates()
      }
    },
  },
  mounted() {
    this.loadTemplatesForCurrentRoute()
  },
}
</script>
<style lang="scss" scoped>
/* Fluid square card: fills its grid column (never a fixed pixel width that would
 * overflow and overlap neighbours), capped so it matches the current desktop
 * size on wide screens. aspect-ratio keeps it square as the column width flexes. */
.template-card {
  /* Slightly narrower than the column + centered, so there is always a clear
   * horizontal gap between cards as they compact, independent of grid gutters. */
  width: calc(100% - 1rem);
  max-width: 342px;
  margin: 0 auto;
  aspect-ratio: 1 / 1;
  cursor: pointer;

  &:hover {
    background: #eaf3ff 0% 0% no-repeat padding-box;
    box-shadow: 0px 10px 25px #00429d0d;
  }
}

.template-card__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
  padding: 1rem;
  overflow: hidden;
}

.template-card__icon {
  width: 55%;
  max-width: 192px;
  height: auto;
  aspect-ratio: 1 / 1;
  object-fit: contain;
}

.template-card__text {
  margin-top: 0.5rem;
  width: 100%;
  min-width: 0;
}

.template-card__title {
  font-weight: bold;
}

.template-card__desc {
  color: #6c757d;
}

/* Phones: compact horizontal "icon : name" rows (like the Select Template list),
 * with side margins so the cards don't touch the screen edges. */
@media (max-width: 575.98px) {
  .template-card {
    width: auto;
    max-width: none;
    height: auto;
    aspect-ratio: auto;
    margin: 0 1.25rem;
  }

  .template-card__inner {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    text-align: left;
    padding: 0.65rem 0.9rem;
  }

  .template-card__icon {
    width: 44px;
    max-width: 44px;
    height: 44px;
    aspect-ratio: auto;
    margin: 0 0.85rem 0 0 !important;
    flex: 0 0 auto;
  }

  .template-card__text {
    margin-top: 0;
  }

  .template-card__title {
    font-size: 1rem;
    margin-bottom: 0.1rem;
  }

  .template-card__desc {
    font-size: 0.85rem;
    line-height: 1.2;
  }
}

.first {
  background: #ebedf095;
}
</style>
